import { type createContactFormSchema } from "@/components/features/contact/AddOrEditContactModal/AddContactSlideover";
import {
  Button,
  CountrySelectFormField,
  Form,
  FormSubmitButton,
  PlacesAutocompleteFormField,
  SheetFooter,
  SimpleSelectFormField,
  Slideover,
  StateSelectFormField,
  TextAreaFormField,
  TextFormField,
  Typography,
} from "@/components/ui";
import { FormLoadingOverlay } from "@/components/ui/Form/FormLoadingOverlay";
import { CheckboxFormField } from "@/components/ui/FormFields/CheckboxFormField/CheckboxFormField";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";

import {
  useCreateContactAddress,
  useUpdateContactAddress,
} from "@/lib/data/react-query";
import { ContactAddressSchema } from "@/lib/data/schemas";
import {
  CitySchema,
  CountrySchema,
  StateSchema,
  ZipCodeSchema,
} from "@/lib/data/schemas/CommonSchemaFields";
import { CONTACT_ADDRESS_LABELS } from "@/lib/data/static/contactStatics";
import { type ContactAddress } from "@/lib/types";
import { ContactAddressIcon } from "@/lib/utils/iconUtils";
import { cn } from "@/lib/utils/cn";
import { slideoverFieldsetClassName } from "@/lib/utils/slideover-classes";
import { zodResolver } from "@hookform/resolvers/zod";
import dayjs from "dayjs";
import { useEffect } from "react";
import { useForm, type UseFormReturn } from "react-hook-form";
import { z } from "zod";

interface AddOrEditContactAddressSlideoverProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  editing: boolean;
  data?: ContactAddress;
  contactId: string;
  onDelete?: () => void;
  hasEditPermission?: boolean;
  hasDeletePermission?: boolean;
  parentForm?: UseFormReturn<z.infer<typeof createContactFormSchema>>;
  variant?: "slideover" | "inline";
}

type ConditionalProps =
  | {
      editing: true;
      activeContactAddress: Pick<ContactAddress, "id" | "dateModified"> &
        Partial<ContactAddress>;
    }
  | {
      editing: false;
      activeContactAddress?: never;
    };

const formSchema = ContactAddressSchema.omit({
  contactId: true,
  firmId: true,
  dateCreated: true,
  id: true,
}).extend({
  city: z.union([
    z.literal(""),
    CitySchema,
    z.null().transform(() => ""),
    z.undefined().transform(() => ""),
  ]),
  county: z.string().nullable(),
  country: z.union([
    z.literal(""),
    CountrySchema,
    z.null().transform(() => ""),
    z.undefined().transform(() => ""),
  ]),
  description: z.string(),
  isPrimary: z.boolean(),
  label: z.string(),
  state: z.union([
    z.literal(""),
    StateSchema,
    z.null().transform(() => ""),
    z.undefined().transform(() => ""),
  ]),
  street1: z.string(),
  street2: z.string(),
  zipCode: z.union([
    z.literal(""),
    ZipCodeSchema,
    z.null().transform(() => ""),
    z.undefined().transform(() => ""),
  ]),
});

const defaultObject: z.output<typeof formSchema> = {
  city: "",
  county: null,
  state: "",
  street1: "",
  street2: "",
  zipCode: "",
  country: "United States",
  description: "",
  label: "",
  isPrimary: false,
  dateModified: "",
};

export const AddOrEditContactAddressSlideover = ({
  activeContactAddress,
  contactId,
  editing,
  hasDeletePermission,
  hasEditPermission,
  onDelete,
  open,
  parentForm,
  setOpen,
  variant = "slideover",
}: AddOrEditContactAddressSlideoverProps & ConditionalProps) => {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    values: editing
      ? {
          ...defaultObject,
          ...activeContactAddress,
          city: activeContactAddress.city ?? "",
          country: activeContactAddress.country || "United States",
          county: activeContactAddress.county ?? null,
          description: activeContactAddress.description ?? "",
          isPrimary: activeContactAddress.isPrimary ?? false,
          label: activeContactAddress.label ?? "",
          state: activeContactAddress.state ?? "",
          street1: activeContactAddress.street1 ?? "",
          street2: activeContactAddress.street2 ?? "",
          zipCode: activeContactAddress.zipCode ?? "",
        }
      : defaultObject,
    mode: "onSubmit",
  });
  const createContactAddress = useCreateContactAddress();
  const updateContactAddress = useUpdateContactAddress();
  const isFormLoading =
    createContactAddress.isPending || updateContactAddress.isPending;

  const normalizeAddressRow = (d: z.infer<typeof formSchema>) => ({
    ...d,
    street2: d.street2,
  });

  const onSubmitParentForm = (data: z.infer<typeof formSchema>) => {
    if (!parentForm) return;
    const row = normalizeAddressRow(data);
    if (editing) {
      const existingAddresses = parentForm.getValues("addresses") ?? [];
      parentForm.setValue(
        "addresses",
        existingAddresses.map((address) =>
          address.id === activeContactAddress.id
            ? {
                ...row,
                id: activeContactAddress.id,
              }
            : address,
        ),
        { shouldDirty: true },
      );
      return data;
    } else {
      const existingAddresses = parentForm.getValues("addresses") ?? [];
      parentForm.setValue(
        "addresses",
        [
          ...existingAddresses,
          {
            ...row,
            id: Math.random().toString(),
            dateModified: dayjs().toISOString(),
          },
        ],
        { shouldDirty: true },
      );
      return data;
    }
  };

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    if (parentForm) {
      onSubmitParentForm(data);
      setOpen(false);
      return;
    }
    if (editing) {
      const dirtyKeys = Object.keys(form.formState.dirtyFields) as Array<
        keyof z.infer<typeof formSchema>
      >;
      const dirtyData: Partial<ContactAddress> = {};
      for (const key of dirtyKeys) {
        (dirtyData as Record<string, unknown>)[key] = data[key];
      }
      if ("street1" in dirtyData) {
        dirtyData.street1 = data.street1.trim() || null;
      }
      await updateContactAddress.mutateAsync({
        contactId,
        id: activeContactAddress.id,
        ...dirtyData,
        dateModified: activeContactAddress.dateModified,
      });
    } else {
      await createContactAddress
        .mutateAsync({
          contactId,
          ...data,
          street1: data.street1.trim() || null,
          dateModified: undefined,
        })
        .then(() => {
          form.reset();
        });
    }
    setOpen(false);
  };

  useEffect(() => {
    if (!open) {
      form.reset();
    }
  }, [open, form]);

  const description = editing
    ? "Update the contact address details below"
    : "Create a new address for this contact";
  const title = editing ? "Update address" : "Create address";
  const isInline = variant === "inline";
  const fieldsetClassName = cn(
    slideoverFieldsetClassName,
    isInline && "p-0 my-2 gap-3",
  );
  const handleInlineSubmit = () => {
    void form.handleSubmit(onSubmit)();
  };
  const formContent = (
    <Form {...form}>
      <form
        className={cn(
          "relative flex grow flex-col",
          isInline ? "overflow-visible" : "overflow-hidden",
        )}
        onSubmit={async (e) => {
          e.preventDefault();
          e.stopPropagation();
          await form.handleSubmit(onSubmit)();
        }}
      >
        {isFormLoading && <FormLoadingOverlay />}
        <div
          className={cn(
            "overflow-y-auto",
            variant === "inline" && "overflow-visible",
          )}
        >
          <fieldset
            disabled={editing ? !hasEditPermission : false}
            className={fieldsetClassName}
          >
            <SimpleSelectFormField
              placeholder="Select a label"
              label="Label"
              register={form.register("label")}
              items={CONTACT_ADDRESS_LABELS}
            />
            <CheckboxFormField
              register={form.register("isPrimary")}
              label="Primary address"
              className={variant === "inline" ? "mt-0 h-auto" : undefined}
            />
            <PlacesAutocompleteFormField
              register={form.register("street1")}
              defaultValue={form.getValues("street1")}
              label="Address 1"
              onAddressSelect={(address) => {
                form.setValue("city", address.city, {
                  shouldValidate: true,
                  shouldDirty: true,
                });
                form.setValue("country", address.country, {
                  shouldValidate: true,
                });
                form.setValue("state", address.state, {
                  shouldValidate: true,
                });
                form.setValue("zipCode", address.zipCode, {
                  shouldValidate: true,
                });
                form.setValue("street1", address.streetAddress, {
                  shouldValidate: true,
                });
                form.setValue("street2", "");
              }}
            />
            <TextFormField
              register={form.register("street2")}
              label="Address 2"
            />
            <TextFormField
              register={form.register("zipCode")}
              label="Zip code"
            />
            <TextFormField register={form.register("city")} label="City" />
            <StateSelectFormField
              register={form.register("state")}
              label="State"
              name="state"
              country={form.watch("country")}
            />
            <CountrySelectFormField
              register={form.register("country")}
              label="Country"
              name="country"
              stateSelectName="state"
            />

            <div className="col-span-2">
              <TextAreaFormField
                register={form.register("description")}
                label="Description"
              />
            </div>
          </fieldset>
        </div>
        <div className="flex grow" />
        <SheetFooter addSlideoverStyles={variant === "slideover"}>
          {hasDeletePermission && editing && (
            <Button type="button" variant="destructive" onClick={onDelete}>
              Delete
            </Button>
          )}
          <div className="flex grow justify-end gap-x-2">
            <FormCancelButton
              setOpen={setOpen}
              isDirty={form.formState.isDirty}
            />
            <FormSubmitButton
              type={isInline ? "button" : "submit"}
              submitFn={isInline ? handleInlineSubmit : undefined}
              overrideDisabled={!editing}
            />
          </div>
        </SheetFooter>
      </form>
    </Form>
  );

  if (variant === "inline") {
    if (!open) return null;
    return (
      <div className="space-y-4">
        <div className="flex items-start gap-2">
          <ContactAddressIcon className="h-5 w-5 text-gray-400" />
          <div className="space-y-1">
            <Typography weight="medium">{title}</Typography>
            <Typography variant="subHeading" className="text-muted-foreground">
              {description}
            </Typography>
          </div>
        </div>
        {formContent}
      </div>
    );
  }

  return (
    <Slideover
      open={open}
      isDirty={form.formState.isDirty}
      setOpen={setOpen}
      title={title}
      titleIcon={<ContactAddressIcon className="h-4 w-4" />}
      description={description}
      isLoading={isFormLoading}
    >
      {formContent}
    </Slideover>
  );
};
